import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Flame, Dumbbell } from "lucide-react";
import { IMG } from "@/data/menu";
import { scrollToId } from "@/components/site/Reveal";
import FlavourSpotlight from "@/components/site/FlavourSpotlight";

const LINES = ["Fuel Your", "Fitness", "Journey"];

const headline = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
};
const wordVariant = {
  hidden: { y: "115%", opacity: 0, rotate: 4 },
  show: {
    y: "0%",
    opacity: 1,
    rotate: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen pt-20 overflow-hidden bg-noir text-white noise">
      {/* Animated real-can hero backdrop + AI ambient video (poster fallback for slow connections) */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden" data-testid="hero-can-backdrop">
        <video
          data-testid="hero-video"
          className="absolute inset-0 h-full w-full object-cover opacity-45"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={IMG.hero}
        >
          <source src={`${process.env.PUBLIC_URL}/hero-loop.mp4`} type="video/mp4" />
        </video>
        <div className="absolute left-1/2 top-1/3 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-[#DE9A2C]/10 blur-[120px]" />
        {/* Seamless animated RTD-can loop */}
        <motion.div
          data-testid="hero-can-loop"
          className="absolute left-0 top-1/2 flex w-max -translate-y-1/2 items-center gap-14 sm:gap-24"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
        >
          {[
            IMG.canMocha, IMG.canBerry, IMG.canMango, IMG.canVanilla, IMG.canChoco, IMG.canPb,
            IMG.canMocha, IMG.canBerry, IMG.canMango, IMG.canVanilla, IMG.canChoco, IMG.canPb,
          ].map((src, i) => (
            <motion.img
              key={i}
              src={src}
              alt=""
              className="w-28 sm:w-40 shrink-0 opacity-30 drop-shadow-2xl"
              animate={{ y: [0, -18, 0], rotate: [-3, 3, -3] }}
              transition={{ duration: 6 + (i % 3), repeat: Infinity, ease: "easeInOut" }}
            />
          ))}
        </motion.div>
        {/* dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-noir via-noir/85 to-noir/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-noir via-transparent to-noir/60" />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-5rem)] py-12 relative z-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-amber-brand mb-7"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-amber-brand animate-pulseDot" />
            The Fuel Lounge&trade; · Fitness Nutrition Bar
          </motion.div>

          <motion.h1
            variants={headline}
            initial="hidden"
            animate="show"
            className="font-display font-black uppercase tracking-tight leading-[0.9] text-white text-6xl sm:text-7xl lg:text-8xl"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.16em] -mb-[0.1em]">
                <span className="flex flex-wrap gap-x-[0.22em]">
                  {line.split(" ").map((word, wi) => (
                    <motion.span
                      key={`${word}-${wi}`}
                      variants={wordVariant}
                      whileHover={i === 1 ? { scale: 1.04 } : { y: -4 }}
                      transition={{ type: "spring", stiffness: 300, damping: 18 }}
                      className={`inline-block origin-bottom will-change-transform ${i === 1 ? "text-sheen" : ""}`}
                    >
                      {word}
                    </motion.span>
                  ))}
                </span>
              </span>
            ))}
          </motion.h1>

          <p className="mt-7 max-w-md text-lg text-white/70 leading-relaxed">
            Scientifically formulated pre &amp; post-workout nutrition — ready-to-drink shakes,
            custom blends and build-your-own meals. Every item with its full nutrient breakdown.
          </p>

          <div className="mt-9 flex flex-wrap gap-4">
            <button
              data-testid="hero-pre-cta"
              onClick={() => scrollToId("menu")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#DE9A2C] hover:bg-[#c9861f] text-[#17120C] font-bold px-7 py-3.5 transition-colors"
            >
              <Flame size={18} /> Pre-Workout
              <ArrowDown className="group-hover:translate-y-0.5 transition-transform" size={16} />
            </button>
            <button
              data-testid="hero-post-cta"
              onClick={() => scrollToId("menu")}
              className="group inline-flex items-center gap-2 rounded-full border border-white/30 hover:border-white text-white font-semibold px-7 py-3.5 transition-colors"
            >
              <Dumbbell size={18} /> Post-Workout
            </button>
          </div>

          <div className="mt-12 flex gap-8">
            {[["32g", "Max Protein"], ["6", "RTD Flavours"], ["100%", "Macros Shown"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-mono text-3xl font-semibold text-white">{n}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-white/45 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[3rem] overflow-hidden border border-white/10 h-[420px] sm:h-[560px]"
          >
            <motion.img
              src={IMG.hero}
              alt="The Fuel Lounge at ProteinCafe"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[130%] object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <FlavourSpotlight />
        </div>
      </div>
    </section>
  );
}
